/** @format */

import React from "react";
import {
	Box,
	Container,
	Typography,
	Grid,
	Card,
	CardContent,
	Button,
	styled,
	Paper,
} from "@mui/material";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import InsightsIcon from "@mui/icons-material/Insights";
import BarChartIcon from "@mui/icons-material/BarChart";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import ShieldIcon from "@mui/icons-material/Shield";
import {
	ArrowForward,
	Assessment,
	Create,
	TrackChanges,
	EmojiEvents,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { colors } from "../../utility/types/constant";
import { CTAButtonsWrapper, PrimaryCTAButton } from "../banner-one/styles";

const HeroSection = styled(Box)(({ theme }) => ({
	background: colors.primary,
	color: "#fff",
	padding: "80px 0",
	textAlign: "center",
	fontFamily: "'Montserrat', sans-serif",
}));

const FeatureCard = styled(Card)(({ theme }) => ({
	height: "100%",
	display: "flex",
	flexDirection: "column",
	transition: "transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out",
	"&:hover": {
		transform: "translateY(-8px)",
		boxShadow: "0 12px 24px rgba(0,0,0,0.15)",
	},
}));

const IconWrapper = styled(Box)(({ theme }) => ({
	width: "64px",
	height: "64px",
	borderRadius: "50%",
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	margin: "0 auto 16px",
	backgroundColor: colors.mainSecondary,
	color: "#fff",
	boxShadow: "0 8px 20px rgba(30, 193, 149, 0.3)",
}));

const StepCard = styled(Paper)(({ theme }) => ({
	backgroundColor: "#ffffff",
	borderRadius: "20px",
	padding: "40px 30px",
	boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
	border: "2px solid rgba(0,0,0,0.05)",
	transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
	textAlign: "center",
	position: "relative",
	overflow: "hidden",
	height: "100%",
	"&::before": {
		content: '""',
		position: "absolute",
		top: 0,
		left: 0,
		right: 0,
		height: "4px",
		background: colors.mainSecondary,
		transform: "scaleX(0)",
		transition: "transform 0.4s ease",
	},
	"&:hover": {
		transform: "translateY(-10px)",
		boxShadow: "0 20px 40px rgba(30, 193, 149, 0.2)",
		borderColor: colors.mainSecondary,
		"&::before": {
			transform: "scaleX(1)",
		},
	},
	[theme.breakpoints.down("md")]: {
		padding: "30px 20px",
	},
}));

const StepNumber = styled(Typography)(({ theme }) => ({
	position: "absolute",
	top: "10px",
	right: "15px",
	fontFamily: "'Montserrat', sans-serif",
	fontSize: "clamp(48px, 6vw, 72px)",
	fontWeight: 900,
	color: "rgba(30, 193, 149, 0.08)",
	lineHeight: 1,
}));

const StepIconWrapper = styled(Box)(({ theme }) => ({
	width: "80px",
	height: "80px",
	borderRadius: "50%",
	backgroundColor: colors.mainSecondary,
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	margin: "0 auto 24px auto",
	color: "#ffffff",
	transition: "all 0.3s ease",
	boxShadow: "0 8px 20px rgba(30, 193, 149, 0.3)",
}));

const StepTitle = styled(Typography)(({ theme }) => ({
	fontFamily: "'Montserrat', sans-serif",
	fontSize: "clamp(1.25rem, 2vw, 1.5rem)",
	fontWeight: 700,
	color: colors.primary,
	marginBottom: "12px",
}));

const StepDescription = styled(Typography)(({ theme }) => ({
	fontFamily: "'Montserrat', sans-serif",
	fontSize: "0.9375rem",
	fontWeight: 400,
	color: "#6c757d",
	lineHeight: 1.7,
}));

const ConnectorLine = styled(Box)(({ theme }) => ({
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	[theme.breakpoints.down("md")]: {
		display: "none",
	},
}));

const AnimatedArrow = styled(Box)(({ theme }) => ({
	color: colors.mainSecondary,
	animation: "slideRight 2s ease-in-out infinite",
	"@keyframes slideRight": {
		"0%, 100%": {
			transform: "translateX(0)",
		},
		"50%": {
			transform: "translateX(10px)",
		},
	},
}));

const CTAButton = styled(Button)(({ theme }) => ({
	backgroundColor: colors.mainSecondary,
	color: "#fff",
	padding: "12px 32px",
	fontSize: "16px",
	fontWeight: 600,
	borderRadius: "8px",
	textTransform: "none",
	"&:hover": {
		backgroundColor: colors.mainSecondary,
		transform: "scale(1.05)",
	},
}));

const FinancialPlanning: React.FC = () => {
	const navigate = useNavigate();
	const handleJoinClick = () => {
		navigate("/circle/sign-up");
	};

	const features = [
		{
			icon: <AccountBalanceWalletIcon sx={{ fontSize: 40 }} />,
			title: "Smart Budgeting Tools",
			description:
				"Create personalized budgets that align with your income and goals. Track spending patterns and identify areas for improvement.",
		},
		{
			icon: <TrendingUpIcon sx={{ fontSize: 40 }} />,
			title: "Goal Setting & Tracking",
			description:
				"Set financial goals for savings, debt reduction, or major purchases. Monitor your progress with visual dashboards.",
		},
		{
			icon: <InsightsIcon sx={{ fontSize: 40 }} />,
			title: "Financial Insights",
			description:
				"Get personalized recommendations based on your spending habits. Discover opportunities to save and grow your wealth.",
		},
		{
			icon: <BarChartIcon sx={{ fontSize: 40 }} />,
			title: "Expense Analytics",
			description:
				"Visualize your spending with detailed charts and reports. Understand where your money goes each month.",
		},
		{
			icon: <LightbulbIcon sx={{ fontSize: 40 }} />,
			title: "Expert Tips & Guidance",
			description:
				"Access curated financial advice and strategies. Learn best practices for saving, investing, and debt management.",
		},
		{
			icon: <ShieldIcon sx={{ fontSize: 40 }} />,
			title: "Secure & Private",
			description:
				"Your financial data is encrypted and protected. Bank-level security ensures your information stays safe.",
		},
	];

	const howItWorksSteps = [
		{
			step: "01",
			icon: <Assessment sx={{ fontSize: 40 }} />,
			title: "Assess Your Finances",
			description:
				"Connect your accounts or manually input your income, expenses, debts, and assets. Get a complete financial snapshot.",
		},
		{
			step: "02",
			icon: <Create sx={{ fontSize: 40 }} />,
			title: "Create Your Plan",
			description:
				"Set financial goals and create a customized budget. Define your priorities and timeline for achieving them.",
		},
		{
			step: "03",
			icon: <TrackChanges sx={{ fontSize: 40 }} />,
			title: "Track Progress",
			description:
				"Monitor your spending and savings in real-time. Receive alerts when you're off track or approaching limits.",
		},
		{
			step: "04",
			icon: <EmojiEvents sx={{ fontSize: 40 }} />,
			title: "Achieve Your Goals",
			description:
				"Celebrate milestones as you reach your targets. Adjust your plan as your financial situation evolves.",
		},
	];

	const benefits = [
		"Take control of your finances with data-driven insights",
		"Build better money habits through consistent tracking",
		"Achieve your financial goals faster with structured planning",
		"Reduce financial stress with clear visibility into your money",
		"Make informed decisions based on your spending patterns",
		"Free tools and resources to improve your financial literacy",
	];

	return (
		<Box sx={{ bgcolor: "#f5f5f5" }}>
			{/* Hero Section */}
			<HeroSection>
				<Container maxWidth="lg">
					<Typography
						variant="h2"
						sx={{
							fontWeight: 700,
							mb: 2,
							fontFamily: "'Montserrat', sans-serif",
						}}>
						Financial Planning
					</Typography>
					<Typography
						variant="h5"
						sx={{
							mb: 4,
							opacity: 0.9,
							fontFamily: "'Montserrat', sans-serif",
						}}>
						Take Control of Your Financial Future
					</Typography>
					<Typography
						variant="h6"
						sx={{
							maxWidth: "800px",
							margin: "0 auto",
							mb: 4,
							fontFamily: "'Montserrat', sans-serif",
						}}>
						Build a personalized financial plan that works for you. Track your
						spending, set meaningful goals, and get actionable insights to make
						smarter money decisions every day.
					</Typography>
					<CTAButtonsWrapper
						data-aos="fade-up"
						data-aos-duration="1000"
						data-aos-delay="400">
						<PrimaryCTAButton
							variant="contained"
							size="large"
							onClick={handleJoinClick}>
							Start Planning Today
						</PrimaryCTAButton>
					</CTAButtonsWrapper>
				</Container>
			</HeroSection>

			{/* What is Financial Planning */}
			<Container maxWidth="lg" sx={{ py: 8 }}>
				<Typography
					variant="h3"
					align="center"
					sx={{
						fontWeight: 800,
						mb: 2,
						color: colors.primary,
						fontFamily: "'Montserrat', sans-serif",
						fontSize: "clamp(2rem, 4vw, 3rem)",
						letterSpacing: "-0.02em",
					}}>
					Why Financial Planning Matters
				</Typography>
				<Typography
					variant="body1"
					align="center"
					sx={{
						maxWidth: "900px",
						margin: "0 auto 6rem",
						fontSize: "clamp(1rem, 2vw, 1.25rem)",
						color: "#6c757d",
						fontFamily: "'Montserrat', sans-serif",
						lineHeight: 1.7,
					}}>
					Financial planning isn't just for the wealthy, it's for everyone who
					wants to make the most of their money. Whether you're paying off debt,
					saving for a home, building an emergency fund, or planning for
					retirement, a solid financial plan helps you stay focused and make
					progress toward your goals. With FundsyFi's tools, you can create a
					roadmap to financial success without needing an expensive advisor.
				</Typography>

				{/* Features Grid */}
				<Grid container spacing={4} sx={{ mb: 8 }}>
					{features.map((feature, index) => (
						<Grid item xs={12} sm={6} md={4} key={index}>
							<FeatureCard>
								<CardContent>
									<IconWrapper>{feature.icon}</IconWrapper>
									<Typography
										variant="h6"
										sx={{ fontWeight: 600, mb: 2, color: "#333" }}>
										{feature.title}
									</Typography>
									<Typography
										variant="body2"
										sx={{ color: "#666", lineHeight: 1.7 }}>
										{feature.description}
									</Typography>
								</CardContent>
							</FeatureCard>
						</Grid>
					))}
				</Grid>
			</Container>

			{/* How It Works */}
			<Box sx={{ bgcolor: "#fff", py: 8 }}>
				<Container maxWidth="lg">
					<Typography
						variant="h3"
						align="center"
						sx={{
							fontWeight: 800,
							mb: 2,
							color: colors.primary,
							fontFamily: "'Montserrat', sans-serif",
							fontSize: "clamp(2rem, 4vw, 3rem)",
							letterSpacing: "-0.02em",
						}}>
						How It Works
					</Typography>
					<Typography
						variant="body1"
						align="center"
						sx={{
							maxWidth: "800px",
							margin: "0 auto 6rem",
							fontSize: "clamp(1rem, 2vw, 1.25rem)",
							color: "#6c757d",
							fontFamily: "'Montserrat', sans-serif",
							lineHeight: 1.7,
						}}>
						Start your financial planning journey in 4 simple steps and build a
						stronger financial future
					</Typography>

					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: {
								xs: "1fr",
								md: "1fr auto 1fr auto 1fr auto 1fr",
							},
							gap: { xs: "40px", md: "20px" },
							alignItems: "center",
						}}>
						{howItWorksSteps.map((item, index) => (
							<React.Fragment key={index}>
								<StepCard elevation={2}>
									<StepNumber>{item.step}</StepNumber>
									<StepIconWrapper>{item.icon}</StepIconWrapper>
									<StepTitle>{item.title}</StepTitle>
									<StepDescription>{item.description}</StepDescription>
								</StepCard>

								{index < howItWorksSteps.length - 1 && (
									<ConnectorLine>
										<AnimatedArrow>
											<ArrowForward sx={{ fontSize: 32 }} />
										</AnimatedArrow>
									</ConnectorLine>
								)}
							</React.Fragment>
						))}
					</Box>
				</Container>
			</Box>

			{/* Benefits Section */}
			<Container maxWidth="lg" sx={{ py: 8 }}>
				<Typography
					variant="h3"
					align="center"
					sx={{
						fontWeight: 800,
						mb: 2,
						color: colors.primary,
						fontFamily: "'Montserrat', sans-serif",
						fontSize: "clamp(2rem, 4vw, 3rem)",
						letterSpacing: "-0.02em",
					}}>
					Why Choose Our Financial Planning Tools?
				</Typography>
				<Typography
					variant="body1"
					align="center"
					sx={{
						maxWidth: "800px",
						margin: "0 auto 6rem",
						fontSize: "clamp(1rem, 2vw, 1.25rem)",
						color: "#6c757d",
						fontFamily: "'Montserrat', sans-serif",
						lineHeight: 1.7,
					}}>
					Empower yourself with the tools and insights to build lasting wealth
				</Typography>

				<Grid
					container
					spacing={3}
					sx={{ maxWidth: "900px", margin: "0 auto" }}>
					{benefits.map((benefit, index) => (
						<Grid item xs={12} sm={6} key={index}>
							<Box sx={{ display: "flex", alignItems: "flex-start" }}>
								<Box
									sx={{
										width: "24px",
										height: "24px",
										borderRadius: "50%",
										backgroundColor: colors.mainSecondary,
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										color: "#fff",
										fontSize: "14px",
										fontWeight: 600,
										mr: 2,
										flexShrink: 0,
										mt: 0.5,
									}}>
									✓
								</Box>
								<Typography
									variant="body1"
									sx={{
										color: "#6c757d",
										lineHeight: 1.7,
										fontFamily: "'Montserrat', sans-serif",
									}}>
									{benefit}
								</Typography>
							</Box>
						</Grid>
					))}
				</Grid>
			</Container>

			{/* CTA Section */}
			<Box
				sx={{
					backgroundColor: colors.primary,
					color: "#fff",
					py: 8,
				}}>
				<Container maxWidth="md" sx={{ textAlign: "center" }}>
					<Typography
						variant="h3"
						sx={{
							fontWeight: 700,
							mb: 2,
							fontFamily: "'Montserrat', sans-serif",
						}}>
						Ready to Transform Your Financial Life?
					</Typography>
					<Typography
						variant="h6"
						sx={{
							mb: 4,
							opacity: 0.9,
							fontFamily: "'Montserrat', sans-serif",
						}}>
						Join FundsyFi today and start building the financial future you
						deserve
					</Typography>
					<Box
						sx={{
							display: "flex",
							gap: 2,
							justifyContent: "center",
							flexWrap: "wrap",
						}}>
						<CTAButton
							onClick={() => navigate("/circle/sign-up")}
							sx={{
								bgcolor: "#fff",
								color: colors.primary,
								"&:hover": {
									bgcolor: "#fff",
								},
							}}>
							Start Planning Free
						</CTAButton>
						<CTAButton
							onClick={() => navigate("/circle/sign-in")}
							sx={{
								bgcolor: "transparent",
								border: "2px solid #fff",
								"&:hover": {
									bgcolor: "rgba(255,255,255,0.1)",
								},
							}}>
							Sign In
						</CTAButton>
					</Box>
				</Container>
			</Box>
		</Box>
	);
};

export default FinancialPlanning;
